import * as React from 'react';
import { AppUser } from 'Src/views/App/App';
import { History, Location } from 'history';
import { Loader } from 'Src/components/Loader/Loader';
import {
  getGeneralMessageNotifications,
  getGeneralMessageNotificationsCount,
  getGeneralMessageRecipientPlanPreview,
  getGeneralMessageRetrySummary,
  getSchoolGeneralMessageDetails,
  retryFailedGeneralMessageDeliveries
} from 'Src/helpers/service/admin/generalMessage';
import { formatChannelStatus } from 'Src/helpers/notifications/formatChannelStatus';
import './GeneralMessageNotifications.css';
import { loadHiddenColumns, saveHiddenColumns } from 'Src/helpers/columnVisibility/columnVisibility';
import {
  PageHelpDefinition,
  PageHelpFilter,
  PageHelpRegistration,
  registerPageHelpDefinition
} from 'Src/helpers/pageHelp/pageHelp';
import { Pagination2 } from 'Src/components/Pagination/Pagination2';
import { ALL_SCHOOL_ROLES_LIST, ROLE_SERVER_TO_CLIENT_MAPPING } from 'Src/consts/user';

interface Props {
  user: AppUser;
  history: History;
  location: Location;
  match?: { params?: { id?: string } };
}

interface Filters {
  email: string;
  phone: string;
  role: string[];
  status: string[];
  channel: string[];
}

interface State {
  items: any[];
  total: number;
  isLoading: boolean;
  isItemsLoading: boolean;
  isFilterOpen: boolean;
  filters: Filters;
  draftFilters: Filters;
  page: number;
  enabledChannels: Array<'email' | 'sms' | 'push'> | null;
  isColumnsOpen: boolean;
  hiddenColumns: string[];
  retryAvailable: number;
  isRetrying: boolean;
  retryNotice: string;
  viewMode: 'DELIVERY' | 'DRAFT_PREVIEW' | 'SCHEDULED_PREVIEW';
  loadError: string;
  message: any;
  searchText: string;
  appliedSearchText: string;
}

const EMPTY_FILTERS: Filters = { email: '', phone: '', role: [], status: [], channel: [] };
const PAGE_SIZE = 20;
const CHANNEL_FILTER_OPTIONS = [
  { value: 'EMAIL', label: 'Email' },
  { value: 'SMS', label: 'SMS' },
  { value: 'PUSH', label: 'Push' }
];
const DELIVERY_STATUS_FILTER_OPTIONS = [
  { value: 'DELIVERED', label: 'Delivered' },
  { value: 'PENDING', label: 'Pending' },
  { value: 'FAILED', label: 'Failed' },
  { value: 'UNAVAILABLE', label: 'Unavailable' }
];
const PREVIEW_STATUS_FILTER_OPTIONS = [
  { value: 'PLANNED', label: 'Planned' },
  { value: 'UNAVAILABLE', label: 'Unavailable' }
];
const ROLE_FILTER_OPTIONS = ALL_SCHOOL_ROLES_LIST.map(value => ({
  value,
  label: ROLE_SERVER_TO_CLIENT_MAPPING[value] || value
}));
const COLUMNS = [
  { key: 'user', label: 'User' },
  { key: 'role', label: 'Role' },
  { key: 'email', label: 'Email' },
  { key: 'phone', label: 'Phone' },
  { key: 'delivery', label: 'Channels and delivery' }
];
interface ContactValue {
  value: string;
  source: 'Default' | 'MIS';
}

export class GeneralMessageNotifications extends React.Component<Props, State> {
  state: State = {
    items: [],
    total: 0,
    isLoading: true,
    isItemsLoading: false,
    isFilterOpen: false,
    filters: { ...EMPTY_FILTERS },
    draftFilters: { ...EMPTY_FILTERS },
    page: 1,
    enabledChannels: null,
    isColumnsOpen: false,
    hiddenColumns: loadHiddenColumns(this.props.user, 'communications.generalMessageNotifications'),
    retryAvailable: 0,
    isRetrying: false,
    retryNotice: '',
    viewMode: 'DELIVERY',
    loadError: '',
    message: null,
    searchText: '',
    appliedSearchText: ''
  };

  private pageHelpRegistration?: PageHelpRegistration;
  private pageHelpSignature = '';
  private deliveryRequestId = 0;

  private setHiddenColumns = (hiddenColumns: string[]): void => {
    saveHiddenColumns(this.props.user, 'communications.generalMessageNotifications', hiddenColumns);
    this.setState({ hiddenColumns });
  };

  componentDidMount() {
    this.pageHelpRegistration = registerPageHelpDefinition(this.getPageHelpDefinition());
    this.pageHelpSignature = this.getPageHelpSignature();
    document.addEventListener('mousedown', this.onDocumentMouseDown);
    document.addEventListener('keydown', this.onDocumentKeyDown);
    this.loadItems();
  }

  componentDidUpdate() {
    if (!this.pageHelpRegistration) return;
    const signature = this.getPageHelpSignature();
    if (signature === this.pageHelpSignature) return;
    this.pageHelpSignature = signature;
    this.pageHelpRegistration.update(this.getPageHelpDefinition());
  }

  componentWillUnmount() {
    this.deliveryRequestId += 1;
    if (this.pageHelpRegistration) this.pageHelpRegistration.unregister();
    document.removeEventListener('mousedown', this.onDocumentMouseDown);
    document.removeEventListener('keydown', this.onDocumentKeyDown);
  }

  private getMessageId = (): string => {
    const routeId = this.props.match && this.props.match.params && this.props.match.params.id;
    if (routeId) return routeId;
    return new URLSearchParams(this.props.location.search).get('generalMessageId') || '';
  };

  private loadItems = (): void => {
    const messageId = this.getMessageId();
    this.setState({ loadError: '' });
    const loadRecipientPreview = () =>
      getGeneralMessageRecipientPlanPreview(this.props.user, messageId).then(preview => {
        const status = String(preview.status || '').toUpperCase();
        this.setState({
          items: Array.isArray(preview.affectedUserList) ? preview.affectedUserList : [],
          total: Array.isArray(preview.affectedUserList) ? preview.affectedUserList.length : 0,
          enabledChannels: Array.isArray(preview.enabledChannels) ? preview.enabledChannels : [],
          retryAvailable: 0,
          viewMode: status === 'SCHEDULED' ? 'SCHEDULED_PREVIEW' : 'DRAFT_PREVIEW',
          message: preview.message || { id: messageId, status },
          isLoading: false
        });
      });
    const isRecipientPreviewRequested =
      new URLSearchParams(this.props.location.search).get('view') === 'recipient-preview';
    const request = isRecipientPreviewRequested
      ? loadRecipientPreview()
      : Promise.all([
          getSchoolGeneralMessageDetails(this.props.user, messageId).catch(() => null),
          getGeneralMessageRetrySummary(this.props.user, messageId).catch(() => ({ statuses: {}, retryAvailable: 0 }))
        ]).then(([message, retrySummary]) => {
          const status = String(message && message.status ? message.status : '').toUpperCase();
          if (status === 'DRAFT' || status === 'SCHEDULED') return loadRecipientPreview();

          this.setState(
            {
              enabledChannels: message ? this.getEnabledChannels(message) : null,
              retryAvailable: Number(retrySummary.retryAvailable) || 0,
              viewMode: 'DELIVERY',
              message,
              isLoading: false
            },
            this.loadDeliveryItems
          );
        });

    request.catch(() =>
      this.setState({
        items: [],
        total: 0,
        isLoading: false,
        isItemsLoading: false,
        loadError: 'Recipient and delivery details could not be loaded. Please try again.'
      })
    );
  };

  private getDeliveryWhere = (): any => {
    const { filters, appliedSearchText } = this.state;
    const where: any = {};
    if (appliedSearchText.trim()) where.recipientSearch = appliedSearchText.trim();
    if (filters.email.trim()) where.emailSearch = filters.email.trim();
    if (filters.phone.trim()) where.phoneSearch = filters.phone.trim();
    if (filters.role.length > 0) where.role = filters.role;
    if (filters.status.length > 0) where.deliveryStatus = filters.status;
    if (filters.channel.length > 0) where.channel = filters.channel;
    return where;
  };

  private loadDeliveryItems = (): void => {
    if (this.state.viewMode !== 'DELIVERY') return;
    const requestId = ++this.deliveryRequestId;
    const messageId = this.getMessageId();
    const where = this.getDeliveryWhere();
    const filter = {
      where,
      skip: Math.max(0, (this.state.page - 1) * PAGE_SIZE),
      limit: PAGE_SIZE,
      order: 'createdAt DESC'
    };

    this.setState({ isItemsLoading: true, loadError: '' });
    Promise.all([
      getGeneralMessageNotifications(this.props.user, messageId, filter),
      getGeneralMessageNotificationsCount(this.props.user, messageId, where)
    ])
      .then(([items, countResult]) => {
        if (requestId !== this.deliveryRequestId) return;
        const total = Number(countResult && countResult.count) || 0;
        const lastPage = Math.max(1, Math.ceil(total / PAGE_SIZE));
        if (this.state.page > lastPage) {
          this.setState({ page: lastPage }, this.loadDeliveryItems);
          return;
        }
        this.setState({
          items: Array.isArray(items) ? items : [],
          total,
          isItemsLoading: false
        });
      })
      .catch(() => {
        if (requestId !== this.deliveryRequestId) return;
        this.setState({
          items: [],
          total: 0,
          isItemsLoading: false,
          loadError: 'Recipient and delivery details could not be loaded. Please try again.'
        });
      });
  };

  private setCurrentPage = (page: number): void => {
    this.setState({ page }, () => {
      if (this.state.viewMode === 'DELIVERY') this.loadDeliveryItems();
    });
  };

  private onDocumentMouseDown = (event: MouseEvent): void => {
    const target = event.target as HTMLElement;
    if (!target.closest('.gmnFilter') && !target.closest('.gmnFilterButton')) {
      this.setState(state => ({
        isFilterOpen: false,
        draftFilters: { ...state.filters }
      }));
    }
    if (!target.closest('.gmnColumns')) this.setState({ isColumnsOpen: false });
  };

  private onDocumentKeyDown = (event: KeyboardEvent): void => {
    if (event.key === 'Escape') {
      this.setState(state => ({
        isFilterOpen: false,
        draftFilters: { ...state.filters },
        isColumnsOpen: false
      }));
    }
  };

  private returnToMessage = (): void => {
    const messageId = this.getMessageId();
    const status = String(
      (this.state.message && this.state.message.status) ||
        (this.state.viewMode === 'SCHEDULED_PREVIEW'
          ? 'SCHEDULED'
          : this.state.viewMode === 'DRAFT_PREVIEW'
          ? 'DRAFT'
          : 'SENT')
    ).toUpperCase();
    const routeState = (this.props.location.state || {}) as any;

    this.props.history.push({
      pathname: '/generalMessages',
      search: routeState.returnSearch || '',
      state: {
        activeStatus: status,
        message: {
          id: messageId,
          status
        }
      }
    });
  };

  private getUser = (item: any): any => item.user || item;

  private getEnabledChannels = (message: any): Array<'email' | 'sms' | 'push'> => {
    const content = message.content || {};
    const channels: Array<'email' | 'sms' | 'push'> = [];

    if (content.email && (content.email.subject || content.email.body)) channels.push('email');
    if (content.sms && content.sms.text) channels.push('sms');
    if (content.push && (content.push.title || content.push.body)) channels.push('push');

    return channels;
  };

  private getVisibleChannelStatuses = (item: any): any[] => {
    if (this.state.viewMode !== 'DELIVERY') {
      const enabledChannels = this.state.enabledChannels || [];
      const channelFlags = item.channels || {};
      const availability = {
        email: Boolean(channelFlags.isEmailChannelsExist),
        sms: Boolean(channelFlags.isSmsChannelsExist),
        push: Boolean(channelFlags.isPushChannelsExist)
      };

      return enabledChannels.map(channel => ({
        channelType: channel.toUpperCase(),
        status: availability[channel] ? 'SCHEDULED' : 'NO_CHANNELS',
        isRecipientPreview: true
      }));
    }

    const channelStatuses = Array.isArray(item.channelStatus) ? item.channelStatus : [];
    const { enabledChannels } = this.state;

    const visibleChannelStatuses =
      enabledChannels === null
        ? channelStatuses
        : channelStatuses.filter(status => {
            const channel = formatChannelStatus(status).key;
            return channel !== 'unknown' && enabledChannels.indexOf(channel) !== -1;
          });

    if (visibleChannelStatuses.length > 0 || !item.deliveryStatus) return visibleChannelStatuses;
    return [
      {
        deliveryStatus: item.deliveryStatus,
        isNotificationFallback: true,
        suppressionReason: item.details && item.details.deliverySuppressionReason
      }
    ];
  };

  private getRoles = (item: any): string[] => {
    const user = this.getUser(item);
    const permissionPresets = Array.isArray(item.permissionPresets) ? item.permissionPresets.filter(Boolean) : [];
    const permissionIds = [item.permissionId, ...(Array.isArray(item.permissionIds) ? item.permissionIds : [])]
      .filter(Boolean)
      .map(String);
    const permissions = Array.isArray(user.permissions) ? user.permissions : [];
    const matchingPermissions = permissionIds.length
      ? permissions.filter(permission => {
          const permissionId = permission && (permission.id || permission._id);
          return permissionId && permissionIds.indexOf(String(permissionId)) !== -1;
        })
      : permissions;
    const fallbackPresets = matchingPermissions.map(permission => permission && permission.preset).filter(Boolean);
    const roles =
      permissionPresets.length > 0 ? permissionPresets : fallbackPresets.length > 0 ? fallbackPresets : [user.preset];
    return roles
      .map(role =>
        String(role || '')
          .trim()
          .toUpperCase()
      )
      .filter((role, index, all) => Boolean(role) && all.indexOf(role) === index);
  };

  private getRole = (item: any): string => {
    const roles = this.getRoles(item);
    return roles.length > 0 ? roles.map(role => ROLE_SERVER_TO_CLIENT_MAPPING[role] || role).join(', ') : '—';
  };

  private getContactValues = (item: any, type: 'email' | 'phone'): ContactValue[] => {
    const user = this.getUser(item);
    const defaultValue = typeof user[type] === 'string' ? user[type].trim() : '';
    const misKey = `mis${type.charAt(0).toUpperCase()}${type.slice(1)}`;
    const misValue = typeof user[misKey] === 'string' ? user[misKey].trim() : '';
    const values: ContactValue[] = [];
    if (defaultValue) values.push({ value: defaultValue, source: 'Default' });
    if (misValue && misValue !== defaultValue) values.push({ value: misValue, source: 'MIS' });
    return values;
  };

  private renderContacts = (contacts: ContactValue[], type: 'email' | 'phone'): React.ReactNode =>
    contacts.length > 0 ? (
      <div className="gmnContacts">
        {contacts.map(contact => (
          <span key={`${contact.source}-${contact.value}`}>
            {type === 'email' ? (
              <a href={`mailto:${contact.value}`}>{contact.value}</a>
            ) : (
              <a href={`tel:${contact.value}`}>{contact.value}</a>
            )}
          </span>
        ))}
      </div>
    ) : (
      <span className="gmnNoContact">—</span>
    );

  private getFilteredItems = (): any[] => {
    const { filters, items } = this.state;
    const name = this.state.appliedSearchText.trim().toLowerCase();
    return items.filter(item => {
      const user = this.getUser(item);
      const fullName = `${user.firstName || ''} ${user.lastName || ''}`.toLowerCase();
      const emails = this.getContactValues(item, 'email')
        .map(contact => contact.value)
        .join(' ')
        .toLowerCase();
      const phones = this.getContactValues(item, 'phone')
        .map(contact => contact.value)
        .join(' ')
        .toLowerCase();
      const roles = this.getRoles(item);
      const channelDeliveries = this.getVisibleChannelStatuses(item).map(status => {
        const formatted = formatChannelStatus(status);
        const deliveryStatus =
          this.state.viewMode !== 'DELIVERY' && formatted.status === 'pending'
            ? 'PLANNED'
            : formatted.status.toUpperCase();
        return { channel: formatted.key.toUpperCase(), status: deliveryStatus };
      });
      const matchesChannelAndStatus =
        filters.channel.length > 0 && filters.status.length > 0
          ? channelDeliveries.some(
              delivery =>
                filters.channel.indexOf(delivery.channel) !== -1 && filters.status.indexOf(delivery.status) !== -1
            )
          : (filters.channel.length === 0 ||
              channelDeliveries.some(delivery => filters.channel.indexOf(delivery.channel) !== -1)) &&
            (filters.status.length === 0 ||
              channelDeliveries.some(delivery => filters.status.indexOf(delivery.status) !== -1));
      return (
        (!name || fullName.indexOf(name) !== -1) &&
        (!filters.email || emails.indexOf(filters.email.trim().toLowerCase()) !== -1) &&
        (!filters.phone || phones.indexOf(filters.phone.trim().toLowerCase()) !== -1) &&
        (filters.role.length === 0 || filters.role.some(role => roles.indexOf(role) !== -1)) &&
        matchesChannelAndStatus
      );
    });
  };

  private applyFilters = (): void => {
    this.setState(
      state => ({
        filters: { ...state.draftFilters },
        isFilterOpen: false,
        page: 1
      }),
      this.loadDeliveryItems
    );
  };

  private clearFilters = (): void => {
    this.setState(
      {
        filters: { ...EMPTY_FILTERS },
        draftFilters: { ...EMPTY_FILTERS },
        isFilterOpen: false,
        page: 1
      },
      this.loadDeliveryItems
    );
  };

  private closeFilters = (): void => {
    this.setState(state => ({
      isFilterOpen: false,
      draftFilters: { ...state.filters }
    }));
  };

  private toggleDraftFilterValue = (key: 'role' | 'status' | 'channel', value: string): void => {
    this.setState(state => {
      const values = state.draftFilters[key];
      return {
        draftFilters: {
          ...state.draftFilters,
          [key]: values.indexOf(value) === -1 ? [...values, value] : values.filter(item => item !== value)
        }
      };
    });
  };

  private removeFilter = (key: 'email' | 'phone'): void => {
    this.setState(
      state => ({
        filters: { ...state.filters, [key]: '' },
        draftFilters: { ...state.filters, [key]: '' },
        page: 1
      }),
      this.loadDeliveryItems
    );
  };

  private removeFilterValue = (key: 'role' | 'status' | 'channel', value: string): void => {
    this.setState(
      state => ({
        filters: { ...state.filters, [key]: state.filters[key].filter(item => item !== value) },
        draftFilters: { ...state.filters, [key]: state.filters[key].filter(item => item !== value) },
        page: 1
      }),
      this.loadDeliveryItems
    );
  };

  private retryFailedDeliveries = (): void => {
    if (this.state.isRetrying || this.state.retryAvailable < 1) return;

    this.setState({ isRetrying: true, retryNotice: '' });
    retryFailedGeneralMessageDeliveries(this.props.user, this.getMessageId())
      .then(result => {
        const queued = Number(result.queued) || 0;
        this.setState({
          isRetrying: false,
          retryAvailable: 0,
          retryNotice:
            queued > 0
              ? `${queued} ${queued === 1 ? 'delivery has' : 'deliveries have'} been queued for retry.`
              : 'There are no deliveries that can be safely retried.'
        });
        this.loadItems();
      })
      .catch(() =>
        this.setState({
          isRetrying: false,
          retryNotice: 'The retry request could not be completed. Please try again later.'
        })
      );
  };

  private getMessageTitle = (): string => {
    const message = this.state.message || {};
    return (
      message.title || (message.content && message.content.email && message.content.email.subject) || 'Untitled message'
    );
  };

  private formatDateTime = (value: any): string => {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  private applyNameSearch = (): void => {
    this.setState(state => ({ appliedSearchText: state.searchText, page: 1 }), this.loadDeliveryItems);
  };

  private clearNameSearch = (): void => {
    this.setState({ searchText: '', appliedSearchText: '', page: 1 }, this.loadDeliveryItems);
  };

  private getFilterOptionLabel = (key: 'role' | 'status' | 'channel', value: string): string => {
    const options =
      key === 'role'
        ? ROLE_FILTER_OPTIONS
        : key === 'channel'
        ? CHANNEL_FILTER_OPTIONS
        : this.state.viewMode === 'DELIVERY'
        ? DELIVERY_STATUS_FILTER_OPTIONS
        : PREVIEW_STATUS_FILTER_OPTIONS;
    const option = options.find(item => item.value === value);
    return option ? option.label : value;
  };

  private getActiveFilters = (): Array<{
    key: keyof Filters;
    value?: string;
    label: string;
  }> => {
    const active: Array<{ key: keyof Filters; value?: string; label: string }> = [];
    if (this.state.filters.email.trim()) {
      active.push({ key: 'email', label: `Email: ${this.state.filters.email.trim()}` });
    }
    if (this.state.filters.phone.trim()) {
      active.push({ key: 'phone', label: `Phone: ${this.state.filters.phone.trim()}` });
    }
    (['role', 'status', 'channel'] as Array<'role' | 'status' | 'channel'>).forEach(key => {
      this.state.filters[key].forEach(value => {
        const prefix = key === 'status' ? 'Delivery status' : key.charAt(0).toUpperCase() + key.slice(1);
        active.push({ key, value, label: `${prefix}: ${this.getFilterOptionLabel(key, value)}` });
      });
    });
    return active;
  };

  private getPageHelpSignature = (): string =>
    JSON.stringify({
      viewMode: this.state.viewMode,
      isLoading: this.state.isLoading,
      loadError: this.state.loadError,
      itemCount: this.state.items.length,
      filteredCount: this.getFilteredItems().length,
      filters: this.state.filters,
      searchText: this.state.appliedSearchText,
      page: this.state.page,
      retryAvailable: this.state.retryAvailable,
      title: this.getMessageTitle()
    });

  private getPageHelpDefinition = (): PageHelpDefinition => {
    const isRecipientPreview = this.state.viewMode !== 'DELIVERY';
    const isScheduledPreview = this.state.viewMode === 'SCHEDULED_PREVIEW';
    const filteredCount = this.getFilteredItems().length;
    const pageCount = filteredCount > 0 ? Math.ceil(filteredCount / PAGE_SIZE) : 0;
    const currentPage = Math.min(this.state.page, Math.max(pageCount, 1));
    const filters: PageHelpFilter[] = this.getActiveFilters().map((filter, index) => ({
      id: `${filter.key}-${filter.value || index}`,
      label: filter.key === 'status' ? 'Delivery status' : filter.key.charAt(0).toUpperCase() + filter.key.slice(1),
      value: filter.value
        ? this.getFilterOptionLabel(filter.key as 'role' | 'status' | 'channel', filter.value)
        : filter.label
            .split(': ')
            .slice(1)
            .join(': ')
    }));
    if (this.state.appliedSearchText.trim()) {
      filters.unshift({ id: 'search', label: 'Name', value: this.state.appliedSearchText.trim() });
    }
    const observations: any[] = [
      {
        id: 'list-purpose',
        tone: 'neutral',
        title: isScheduledPreview
          ? 'This is the fixed scheduled audience'
          : isRecipientPreview
          ? 'This is a preview, not a delivery log'
          : 'These are individual delivery records',
        text: isScheduledPreview
          ? 'The recipients were resolved when the message was scheduled. Real delivery statuses appear only after sending begins.'
          : isRecipientPreview
          ? 'Recipients are resolved from the saved audience now. No notification has been sent and the result can change if school data changes.'
          : 'Each row shows the user, the role that included them, available contact details and the outcome for every enabled channel.'
      }
    ];
    if (this.state.retryAvailable > 0) {
      observations.push({
        id: 'safe-retries',
        tone: 'warning',
        title: `${this.state.retryAvailable} ${
          this.state.retryAvailable === 1 ? 'delivery is' : 'deliveries are'
        } ready to retry`,
        text:
          'Retry deliveries queues only failed or temporarily unavailable channels that are safe to attempt again. Successful deliveries are not duplicated.'
      });
    }

    return {
      screenId: isRecipientPreview
        ? isScheduledPreview
          ? 'school.general-messages.recipients.scheduled'
          : 'school.general-messages.recipients.draft'
        : 'school.general-messages.notifications',
      title: isScheduledPreview
        ? 'Scheduled message recipients'
        : isRecipientPreview
        ? 'Draft recipient preview'
        : 'Message notifications',
      subtitle: `Help for “${this.getMessageTitle()}” and the people affected by it.`,
      entity: {
        singular: isRecipientPreview ? 'recipient' : 'notification',
        plural: isRecipientPreview ? 'recipients' : 'notifications'
      },
      state: {
        loading: this.state.isLoading,
        error: this.state.loadError || undefined,
        total: filteredCount,
        visibleCount: Math.min(PAGE_SIZE, filteredCount),
        currentPage,
        totalPages: pageCount,
        activeView: this.state.viewMode,
        filters
      },
      observations,
      anchors: [
        {
          id: 'gm-notifications-header',
          label: 'Message and delivery summary',
          description:
            'Shows the message status, enabled channels, recipient total and delivery totals for this message.'
        },
        {
          id: 'gm-notifications-toolbar',
          label: 'Find and organise records',
          description: 'Search by name, apply detailed filters or choose which columns are visible.'
        },
        {
          id: 'gm-notifications-table',
          label: isRecipientPreview ? 'Planned recipients' : 'Delivery records',
          description: isRecipientPreview
            ? 'Shows who is currently expected to receive the message and which enabled channels are available.'
            : 'Shows the role, contact information and per-channel delivery state for each user.'
        },
        {
          id: 'gm-notifications-pagination',
          label: 'Pagination',
          description: 'Move through the current filtered result set twenty records at a time.'
        }
      ],
      actions: [
        {
          id: 'return-to-message',
          label: 'Back to message',
          description: 'Return to the message details panel and the same message-list view.',
          run: this.returnToMessage
        },
        {
          id: 'open-recipient-filters',
          label: 'Filter this list',
          description: 'Narrow the list by role, contact detail, channel or delivery status.',
          kind: 'primary',
          run: () => this.setState({ isFilterOpen: true, draftFilters: { ...this.state.filters } })
        },
        ...(filters.length > 0
          ? [
              {
                id: 'clear-recipient-filters',
                label: 'Clear search and filters',
                description: 'Show the complete recipient or notification list again.',
                run: () => {
                  this.clearNameSearch();
                  this.clearFilters();
                }
              }
            ]
          : []),
        ...(!isRecipientPreview && this.state.retryAvailable > 0
          ? [
              {
                id: 'retry-failed-deliveries',
                label: `Retry unavailable / failed (${this.state.retryAvailable})`,
                description: 'Queue eligible failed or temporarily unavailable channels for another delivery attempt.',
                run: this.retryFailedDeliveries
              }
            ]
          : [])
      ],
      sections: [
        {
          id: 'roles-and-channels',
          title: 'Why a user appears here',
          summary:
            'Role explains which selected audience or permission brought the user into the message. A user is counted once even when more than one selected role applies.'
        },
        {
          id: 'channel-meaning',
          title: isRecipientPreview ? 'Planned and unavailable channels' : 'Delivery statuses',
          summary: isRecipientPreview
            ? 'Planned means the selected channel is available. Unavailable means that user has no usable address or device channel for it.'
            : 'Delivery is tracked separately for Email, SMS and Push, so one user can have a different result for each enabled channel.'
        }
      ]
    };
  };

  render() {
    if (this.state.isLoading) return <Loader />;

    const isRecipientPreview = this.state.viewMode !== 'DELIVERY';
    const filteredItems = isRecipientPreview ? this.getFilteredItems() : this.state.items;
    const resultCount = isRecipientPreview ? filteredItems.length : this.state.total;
    const pageCount = Math.max(1, Math.ceil(resultCount / PAGE_SIZE));
    const page = Math.min(this.state.page, pageCount);
    const visibleItems = isRecipientPreview
      ? filteredItems.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)
      : filteredItems;
    const activeFilters = this.getActiveFilters();
    const statusFilterOptions = isRecipientPreview ? PREVIEW_STATUS_FILTER_OPTIONS : DELIVERY_STATUS_FILTER_OPTIONS;
    const isScheduledPreview = this.state.viewMode === 'SCHEDULED_PREVIEW';
    const pageTitle = isScheduledPreview
      ? 'Scheduled recipients'
      : isRecipientPreview
      ? 'Draft recipient preview'
      : 'Message notifications';
    const pageDescription = isScheduledPreview
      ? 'Audience fixed when this message was scheduled. Delivery statuses will appear after sending starts.'
      : isRecipientPreview
      ? 'Current recipient preview based on the saved audience settings.'
      : 'Delivery details for every user and enabled channel.';
    const collectionLabel = isRecipientPreview ? 'Planned recipients' : 'Notifications';
    const message = this.state.message || {};
    const messageStatus = String(
      message.status || (isScheduledPreview ? 'SCHEDULED' : isRecipientPreview ? 'DRAFT' : 'SENT')
    ).toUpperCase();
    const enabledChannels = this.state.enabledChannels || [];
    const pageChannelSummary = this.state.items.reduce(
      (summary, item) => {
        this.getVisibleChannelStatuses(item).forEach(status => {
          const formattedStatus = formatChannelStatus(status).status;
          summary[formattedStatus] += 1;
        });
        return summary;
      },
      { delivered: 0, pending: 0, failed: 0, unavailable: 0 }
    );
    const messageStatistics = message.statistics || {};
    const channelSummary = isRecipientPreview
      ? pageChannelSummary
      : enabledChannels.reduce(
          (summary, channel) => {
            const statistics = messageStatistics[channel] || {};
            summary.delivered +=
              Number(statistics.SENT || 0) + Number(statistics.DELIVERED || 0) + Number(statistics.READ || 0);
            summary.pending += Number(statistics.SCHEDULED || 0) + Number(statistics.SENDING || 0);
            summary.failed += Number(statistics.ERROR || 0);
            summary.unavailable +=
              Number(statistics.NO_CHANNELS || 0) +
              Number(statistics.NOT_SENT || 0) +
              Number(statistics.UNSUPPORTED_CHANNEL_TYPE || 0);
            return summary;
          },
          { delivered: 0, pending: 0, failed: 0, unavailable: 0 }
        );
    const scheduledAt = this.formatDateTime(message.scheduleAt);
    const sentAt = this.formatDateTime(message.sentAt);

    return (
      <main className="gmnPage">
        <header className="gmnHeader" data-help-anchor="gm-notifications-header">
          <div className="gmnHeaderPrimary">
            <button type="button" className="gmnBack" onClick={this.returnToMessage}>
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M15 18l-6-6 6-6" />
              </svg>
              Back to message
            </button>
            <span className="gmnHeaderDivider" aria-hidden="true" />
            <div className="gmnHeaderCopy">
              <h1>{pageTitle}</h1>
              <p>
                <strong>{this.getMessageTitle()}</strong>
                <span>{pageDescription}</span>
              </p>
            </div>
          </div>
          <div className="gmnHeaderChips" aria-label="Message recipient summary">
            <span className={`gmnSummaryChip is-status is-${messageStatus.toLowerCase()}`}>
              {messageStatus.charAt(0) + messageStatus.slice(1).toLowerCase()}
            </span>
            <span className="gmnSummaryChip is-count">
              {isRecipientPreview ? this.state.items.length : this.state.total}{' '}
              {isRecipientPreview
                ? this.state.items.length === 1
                  ? 'recipient'
                  : 'recipients'
                : this.state.total === 1
                ? 'notification'
                : 'notifications'}
            </span>
            {enabledChannels.map(channel => (
              <span className={`gmnSummaryChip is-channel is-${channel}`} key={`header-channel-${channel}`}>
                {channel === 'sms' ? 'SMS' : channel.charAt(0).toUpperCase() + channel.slice(1)}
              </span>
            ))}
            {enabledChannels.length === 0 && (
              <span className="gmnSummaryChip is-unavailable">No channels configured</span>
            )}
            {isScheduledPreview && scheduledAt && (
              <span className="gmnSummaryChip is-scheduled">Scheduled {scheduledAt}</span>
            )}
            {!isRecipientPreview && sentAt && <span className="gmnSummaryChip is-sent-at">Sent {sentAt}</span>}
            {isRecipientPreview ? (
              <>
                <span className="gmnSummaryChip is-planned">{channelSummary.pending} planned</span>
                {channelSummary.unavailable > 0 && (
                  <span className="gmnSummaryChip is-unavailable">{channelSummary.unavailable} unavailable</span>
                )}
              </>
            ) : (
              <>
                {channelSummary.delivered > 0 && (
                  <span className="gmnSummaryChip is-delivered">{channelSummary.delivered} delivered</span>
                )}
                {channelSummary.pending > 0 && (
                  <span className="gmnSummaryChip is-pending">{channelSummary.pending} pending</span>
                )}
                {channelSummary.failed > 0 && (
                  <span className="gmnSummaryChip is-failed">{channelSummary.failed} failed</span>
                )}
                {channelSummary.unavailable > 0 && (
                  <span className="gmnSummaryChip is-unavailable">{channelSummary.unavailable} unavailable</span>
                )}
              </>
            )}
          </div>
        </header>

        <section className="gmnContentCard">
          <div className="gmnToolbar" data-help-anchor="gm-notifications-toolbar">
            <strong>
              {collectionLabel} <span>{resultCount}</span>
            </strong>
            <div className="gmnToolbarControls">
              <div className="gmnSearch">
                <input
                  type="search"
                  value={this.state.searchText}
                  placeholder="Search by name or surname"
                  aria-label="Search recipients by name or surname"
                  onChange={event => this.setState({ searchText: event.target.value })}
                  onKeyDown={event => {
                    if (event.key !== 'Enter') return;
                    event.preventDefault();
                    this.applyNameSearch();
                  }}
                />
                {this.state.searchText && (
                  <button type="button" aria-label="Clear recipient search" onClick={this.clearNameSearch}>
                    ×
                  </button>
                )}
              </div>
              <button
                type="button"
                className={`gmnFilterButton ${this.state.isFilterOpen ? 'is-active' : ''}`}
                onClick={() =>
                  this.setState(state => ({
                    isFilterOpen: !state.isFilterOpen,
                    draftFilters: { ...state.filters }
                  }))
                }
              >
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M4 6h16M7 12h10M10 18h4" />
                </svg>
                Filters {activeFilters.length > 0 && <b>{activeFilters.length}</b>}
              </button>
              <div className="gmnColumns">
                <button
                  type="button"
                  className={`gmnButton ${this.state.isColumnsOpen ? 'is-active' : ''}`}
                  onClick={() => this.setState(state => ({ isColumnsOpen: !state.isColumnsOpen }))}
                >
                  <svg viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M4 5h16v14H4zM10 5v14M16 5v14" />
                  </svg>
                  Columns <b>{COLUMNS.length - this.state.hiddenColumns.length}</b>
                </button>
                {this.state.isColumnsOpen && (
                  <div className="gmnColumnsMenu">
                    <header>
                      <strong>Show / hide columns</strong>
                      <button type="button" onClick={() => this.setHiddenColumns([])}>
                        Reset
                      </button>
                    </header>
                    {COLUMNS.map(column => (
                      <label key={column.key}>
                        <input
                          type="checkbox"
                          checked={this.state.hiddenColumns.indexOf(column.key) === -1}
                          onChange={() =>
                            this.setHiddenColumns(
                              this.state.hiddenColumns.indexOf(column.key) === -1
                                ? [...this.state.hiddenColumns, column.key]
                                : this.state.hiddenColumns.filter(key => key !== column.key)
                            )
                          }
                        />
                        {column.key === 'delivery' && isRecipientPreview ? 'Planned channels' : column.label}
                      </label>
                    ))}
                  </div>
                )}
              </div>
            </div>
            {!isRecipientPreview && this.state.retryAvailable > 0 && (
              <button
                type="button"
                className="gmnButton gmnRetryButton"
                disabled={this.state.isRetrying}
                onClick={this.retryFailedDeliveries}
              >
                {this.state.isRetrying ? 'Queuing…' : `Retry unavailable / failed (${this.state.retryAvailable})`}
              </button>
            )}
          </div>

          {this.state.isFilterOpen && (
            <form
              className="gmnFilter"
              onSubmit={event => {
                event.preventDefault();
                this.applyFilters();
              }}
              onKeyDown={event => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  this.applyFilters();
                }
              }}
            >
              <header>
                <strong>Filters</strong>
                <button type="button" aria-label="Close filters" onClick={this.closeFilters}>
                  ×
                </button>
              </header>
              <div className="gmnFilter__body">
                <fieldset className="gmnFilterChoices">
                  <legend>Roles</legend>
                  <div>
                    {ROLE_FILTER_OPTIONS.map(option => {
                      const isSelected = this.state.draftFilters.role.indexOf(option.value) !== -1;
                      return (
                        <button
                          type="button"
                          key={option.value}
                          className={isSelected ? 'is-selected' : ''}
                          aria-pressed={isSelected}
                          onClick={() => this.toggleDraftFilterValue('role', option.value)}
                        >
                          {option.label}
                        </button>
                      );
                    })}
                  </div>
                </fieldset>
                <label>
                  <span>Email</span>
                  <input
                    value={this.state.draftFilters.email}
                    placeholder="Default or MIS email"
                    onChange={event =>
                      this.setState({ draftFilters: { ...this.state.draftFilters, email: event.target.value } })
                    }
                  />
                </label>
                <label>
                  <span>Phone</span>
                  <input
                    value={this.state.draftFilters.phone}
                    placeholder="Default or MIS phone"
                    onChange={event =>
                      this.setState({ draftFilters: { ...this.state.draftFilters, phone: event.target.value } })
                    }
                  />
                </label>
                <fieldset className="gmnFilterChoices">
                  <legend>{isRecipientPreview ? 'Channel availability' : 'Delivery statuses'}</legend>
                  <div>
                    {statusFilterOptions.map(option => {
                      const isSelected = this.state.draftFilters.status.indexOf(option.value) !== -1;
                      return (
                        <button
                          type="button"
                          key={option.value}
                          className={isSelected ? 'is-selected' : ''}
                          aria-pressed={isSelected}
                          onClick={() => this.toggleDraftFilterValue('status', option.value)}
                        >
                          {option.label}
                        </button>
                      );
                    })}
                  </div>
                </fieldset>
                <fieldset className="gmnFilterChoices">
                  <legend>Channels</legend>
                  <div>
                    {CHANNEL_FILTER_OPTIONS.map(option => {
                      const isSelected = this.state.draftFilters.channel.indexOf(option.value) !== -1;
                      return (
                        <button
                          type="button"
                          key={option.value}
                          className={isSelected ? 'is-selected' : ''}
                          aria-pressed={isSelected}
                          onClick={() => this.toggleDraftFilterValue('channel', option.value)}
                        >
                          {option.label}
                        </button>
                      );
                    })}
                  </div>
                </fieldset>
              </div>
              <footer className="gmnFilterActions">
                <button type="button" className="gmnLink is-clear" onClick={this.clearFilters}>
                  Clear all
                </button>
                <div>
                  <button type="button" className="gmnButton" onClick={this.closeFilters}>
                    Cancel
                  </button>
                  <button type="submit" className="gmnButton gmnButton--primary is-apply">
                    Apply
                  </button>
                </div>
              </footer>
            </form>
          )}

          {activeFilters.length > 0 && (
            <div className="gmnChips">
              {activeFilters.map(filter => (
                <span key={`${filter.key}-${filter.value || filter.label}`}>
                  {filter.label}
                  <button
                    type="button"
                    aria-label={`Remove ${filter.label} filter`}
                    onClick={() =>
                      filter.value
                        ? this.removeFilterValue(filter.key as 'role' | 'status' | 'channel', filter.value)
                        : this.removeFilter(filter.key as 'email' | 'phone')
                    }
                  >
                    ×
                  </button>
                </span>
              ))}
              <button type="button" className="gmnLink" onClick={this.clearFilters}>
                Clear all
              </button>
            </div>
          )}

          {this.state.retryNotice && <div className="gmnRetryNotice">{this.state.retryNotice}</div>}
          {this.state.loadError && <div className="gmnRetryNotice is-error">{this.state.loadError}</div>}

          <div className="gmnTableCard" data-help-anchor="gm-notifications-table">
            {this.state.isItemsLoading && <div className="gmnLoadingBar">Updating delivery records…</div>}
            <table>
              <thead>
                <tr>
                  {this.state.hiddenColumns.indexOf('user') === -1 && <th>User</th>}
                  {this.state.hiddenColumns.indexOf('role') === -1 && <th>Role</th>}
                  {this.state.hiddenColumns.indexOf('email') === -1 && <th>Email</th>}
                  {this.state.hiddenColumns.indexOf('phone') === -1 && <th>Phone</th>}
                  {this.state.hiddenColumns.indexOf('delivery') === -1 && (
                    <th>{isRecipientPreview ? 'Planned channels' : 'Channels and delivery'}</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {visibleItems.map((item, itemIndex) => {
                  const user = this.getUser(item);
                  const channelStatus = this.getVisibleChannelStatuses(item);
                  const emails = this.getContactValues(item, 'email');
                  const phones = this.getContactValues(item, 'phone');
                  const role = this.getRole(item);
                  const roleClass = role
                    .split(',')[0]
                    .trim()
                    .toLowerCase()
                    .replace(/[^a-z0-9_-]+/g, '-');
                  return (
                    <tr key={item.id || item._id || `${item.userId || 'recipient'}-${item.permissionId || itemIndex}`}>
                      {this.state.hiddenColumns.indexOf('user') === -1 && (
                        <td>
                          <strong>{`${user.firstName || ''} ${user.lastName || ''}`.trim() || 'Unknown user'}</strong>
                        </td>
                      )}
                      {this.state.hiddenColumns.indexOf('role') === -1 && (
                        <td>
                          <span className={`gmnRole gmnRole--${roleClass}`}>{role}</span>
                        </td>
                      )}
                      {this.state.hiddenColumns.indexOf('email') === -1 && (
                        <td>{this.renderContacts(emails, 'email')}</td>
                      )}
                      {this.state.hiddenColumns.indexOf('phone') === -1 && (
                        <td>{this.renderContacts(phones, 'phone')}</td>
                      )}
                      {this.state.hiddenColumns.indexOf('delivery') === -1 && (
                        <td>
                          <div className="gmnStatuses">
                            {channelStatus.length > 0 ? (
                              channelStatus.map((status, index) => {
                                const formatted = formatChannelStatus(status);
                                const previewStatusLabel =
                                  formatted.status === 'pending' ? 'Planned' : 'No active channel';
                                return (
                                  <div key={`${formatted.key}-${formatted.source}-${index}`} className="gmnStatusGroup">
                                    <span className={`is-${formatted.status}`}>
                                      <i />
                                      {formatted.icon}{' '}
                                      <b>
                                        {formatted.label}
                                        {formatted.source ? ` · ${formatted.source}` : ''}
                                      </b>
                                      — {isRecipientPreview ? previewStatusLabel : formatted.statusLabel}
                                    </span>
                                    {status.suppressionReason && (
                                      <small className="gmnSuppressionReason">{status.suppressionReason}</small>
                                    )}
                                  </div>
                                );
                              })
                            ) : (
                              <span className="is-warning">
                                <i /> ⚠ No channel data
                              </span>
                            )}
                          </div>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {!this.state.loadError && visibleItems.length === 0 && (
              <div className="gmnEmpty">
                {isRecipientPreview
                  ? 'No planned recipients match the selected filters.'
                  : 'No notifications match the selected filters.'}
              </div>
            )}
          </div>

          {resultCount > 0 ? (
            <div className="gmnPaginationModern" data-help-anchor="gm-notifications-pagination">
              <Pagination2
                modern
                currentPage={page}
                total={resultCount}
                limit={PAGE_SIZE}
                setCurrentPageParams={this.setCurrentPage}
              />
            </div>
          ) : null}
        </section>
      </main>
    );
  }
}
